import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChittyModule, PersonaConfig } from '../types';
import { TauriAPI } from '../lib/tauri';
import { 
  Package, 
  Download, 
  CheckCircle, 
  Settings, 
  Loader2,
  ArrowRight
} from 'lucide-react';

interface ModuleManagerProps {
  selectedPersona?: PersonaConfig;
  onSetupComplete: () => void;
}

export const ModuleManager: React.FC<ModuleManagerProps> = ({
  selectedPersona,
  onSetupComplete,
}) => {
  const [modules, setModules] = useState<ChittyModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [installingModules, setInstallingModules] = useState<Set<string>>(new Set());
  const [configuringModules, setConfiguringModules] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const availableModules = await TauriAPI.getAvailableModules();
      setModules(availableModules);
    } catch (error) {
      console.error('Failed to load modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInstallModule = async (moduleId: string) => {
    setInstallingModules(prev => new Set([...prev, moduleId]));
    
    try {
      await TauriAPI.installModule(moduleId);
      // Reload modules to update status
      await loadModules();
    } catch (error) {
      console.error(`Failed to install module ${moduleId}:`, error);
    } finally {
      setInstallingModules(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleId);
        return newSet;
      });
    }
  };

  const handleToggleModule = async (moduleId: string, enabled: boolean) => {
    try {
      await TauriAPI.enableModule(moduleId, enabled);
      setModules(prev => prev.map(module => 
        module.id === moduleId ? { ...module, enabled } : module
      ));
    } catch (error) {
      console.error(`Failed to ${enabled ? 'enable' : 'disable'} module ${moduleId}:`, error);
    }
  };

  const handleConfigureModule = async (moduleId: string) => {
    setConfiguringModules(prev => new Set([...prev, moduleId]));
    
    try {
      // For now, just mark as configured. In a real app, this would open a config dialog
      await new Promise(resolve => setTimeout(resolve, 1000));
      setModules(prev => prev.map(module => 
        module.id === moduleId ? { ...module, config_required: false } : module
      ));
    } catch (error) {
      console.error(`Failed to configure module ${moduleId}:`, error);
    } finally {
      setConfiguringModules(prev => {
        const newSet = new Set(prev);
        newSet.delete(moduleId);
        return newSet;
      });
    }
  };

  const getCategoryIcon = (category: ChittyModule['category']) => {
    switch (category) {
      case 'core': return <Package className="w-5 h-5 text-blue-500" />;
      case 'legal': return <Package className="w-5 h-5 text-purple-500" />;
      case 'financial': return <Package className="w-5 h-5 text-green-500" />;
      case 'property': return <Package className="w-5 h-5 text-orange-500" />;
      case 'ai': return <Package className="w-5 h-5 text-pink-500" />;
      case 'integration': return <Package className="w-5 h-5 text-gray-500" />;
      default: return <Package className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category: ChittyModule['category']) => {
    switch (category) {
      case 'core': return 'bg-blue-100 text-blue-800';
      case 'legal': return 'bg-purple-100 text-purple-800';
      case 'financial': return 'bg-green-100 text-green-800';
      case 'property': return 'bg-orange-100 text-orange-800';
      case 'ai': return 'bg-pink-100 text-pink-800';
      case 'integration': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getModulesByCategory = () => {
    const categories: Record<string, ChittyModule[]> = {};
    modules.forEach(module => {
      if (!categories[module.category]) {
        categories[module.category] = [];
      }
      categories[module.category].push(module);
    });
    return categories;
  };

  const isRecommendedModule = (moduleId: string) => {
    return selectedPersona?.recommended_modules.includes(moduleId) || false;
  };

  const getModuleStatus = (module: ChittyModule) => {
    if (!module.installed) return 'not_installed';
    if (!module.enabled) return 'disabled';
    if (module.config_required) return 'needs_config';
    return 'ready';
  };

  const getStatusBadge = (module: ChittyModule) => {
    const status = getModuleStatus(module);
    
    switch (status) {
      case 'not_installed':
        return <Badge variant="outline">Not Installed</Badge>;
      case 'disabled':
        return <Badge variant="secondary">Disabled</Badge>;
      case 'needs_config':
        return <Badge variant="warning">Needs Config</Badge>;
      case 'ready':
        return <Badge variant="success">Ready</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading ChittyOS modules...</p>
        </div>
      </div>
    );
  }

  const modulesByCategory = getModulesByCategory();
  const totalModules = modules.length;
  const installedModules = modules.filter(m => m.installed).length;
  const readyModules = modules.filter(m => getModuleStatus(m) === 'ready').length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4">Module Management</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {selectedPersona ? 
            `Install and configure modules for your ${selectedPersona.name} persona.` :
            'Install and configure ChittyOS modules for your needs.'
          }
        </p>
      </div>

      {/* Module Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Installation Summary</CardTitle>
          <CardDescription>Overview of your module setup</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{installedModules}/{totalModules}</div>
              <p className="text-sm text-muted-foreground">Modules Installed</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{readyModules}</div>
              <p className="text-sm text-muted-foreground">Ready to Use</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {selectedPersona?.recommended_modules.filter(id => 
                  modules.find(m => m.id === id && getModuleStatus(m) === 'ready')
                ).length || 0}
              </div>
              <p className="text-sm text-muted-foreground">Persona Modules Ready</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modules by Category */}
      {Object.entries(modulesByCategory).map(([category, categoryModules]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getCategoryIcon(category as ChittyModule['category'])}
              {category.charAt(0).toUpperCase() + category.slice(1)} Modules
              <Badge variant="secondary">{categoryModules.length}</Badge>
            </CardTitle>
            <CardDescription>
              Modules in the {category} category
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {categoryModules.map((module) => {
                const isRecommended = isRecommendedModule(module.id);
                const isInstalling = installingModules.has(module.id);
                const isConfiguring = configuringModules.has(module.id);

                return (
                  <div 
                    key={module.id} 
                    className={`p-4 border rounded-lg ${isRecommended ? 'bg-blue-50 border-blue-200' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold">{module.name}</h3>
                          {isRecommended && (
                            <Badge variant="default" className="text-xs">Recommended</Badge>
                          )}
                          <Badge className={`text-xs ${getCategoryColor(module.category)}`}>
                            {module.category}
                          </Badge>
                          {getStatusBadge(module)}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {module.description}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span>v{module.version}</span>
                          {module.install_size && (
                            <span>• {Math.round(module.install_size / 1024 / 1024)} MB</span>
                          )}
                          {module.dependencies.length > 0 && (
                            <span>• {module.dependencies.length} dependencies</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        {!module.installed ? (
                          <Button
                            onClick={() => handleInstallModule(module.id)}
                            disabled={isInstalling}
                            size="sm"
                            className="gap-2"
                          >
                            {isInstalling ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            {isInstalling ? 'Installing...' : 'Install'}
                          </Button>
                        ) : (
                          <>
                            <Button
                              onClick={() => handleToggleModule(module.id, !module.enabled)}
                              variant={module.enabled ? "outline" : "default"}
                              size="sm"
                            >
                              {module.enabled ? 'Disable' : 'Enable'}
                            </Button>
                            
                            {module.config_required && (
                              <Button
                                onClick={() => handleConfigureModule(module.id)}
                                disabled={isConfiguring}
                                variant="secondary"
                                size="sm"
                                className="gap-2"
                              >
                                {isConfiguring ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Settings className="w-4 h-4" />
                                )}
                                {isConfiguring ? 'Configuring...' : 'Configure'}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {module.dependencies.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Dependencies:</p>
                        <div className="flex flex-wrap gap-1">
                          {module.dependencies.map((dep) => (
                            <Badge key={dep} variant="outline" className="text-xs">
                              {dep}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Setup Complete Actions */}
      <Card className="bg-green-50 border-green-200">
        <CardContent className="pt-6">
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Setup Complete!</h3>
            <p className="text-muted-foreground mb-6">
              Your ChittyOS modules are installed and ready to use. You can continue to the dashboard
              or make additional configuration changes.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={loadModules}>
                Refresh Modules
              </Button>
              <Button onClick={onSetupComplete} className="gap-2">
                Go to Dashboard
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};